/**
 * Met les lignes cadeau du panier en accord avec les règles, et joint la liste à la
 * commande en attribut de panier.
 *
 * Les cadeaux sont de vraies lignes à 0 € : c'est la seule façon qu'ils apparaissent
 * au checkout, page Shopify que le thème ne contrôle pas. Le prix barré vient du prix
 * comparé du produit dans le catalogue — le thème l'affiche seul (cart-drawer.js et
 * cart.liquid lisent variant.compare_at_price), aucun code n'est nécessaire pour ça.
 *
 * COMMENT LA BOUCLE EST ÉVITÉE — quatre verrous, dont deux structurels.
 *
 * 1. Idempotence. On ne calcule jamais « ajoute un cadeau », mais l'état voulu :
 *    l'ensemble des règles satisfaites, calculé en Liquid. En face, l'état réel : les
 *    lignes portant _gb_regle. On ne touche au panier que si les deux diffèrent. Notre
 *    propre correction les rend égaux, donc le passage suivant est forcément à vide.
 *    C'est aussi ce qui interdit les doublons et couvre le panier restauré.
 * 2. Aucun cart:updated diffusé. Vérifié dans cart-icon.js:37 : n'importe quel
 *    cart:updated rouvre le tiroir. On repeint par la méthode publique du thème,
 *    cart-drawer.renderCartContents(), qui recale aussi le compteur, sans événement.
 * 3. Un seul traitement à la fois, au niveau du module : sur la page panier le bloc
 *    existe en deux exemplaires, page et tiroir.
 * 4. Un disjoncteur : plafond de corrections par page, plafond de rechargements, et
 *    un cadeau que Shopify refuse n'est jamais retenté.
 *
 * Le cas « la cliente retire le cadeau à la main » est le seul que l'idempotence ne
 * couvre pas : sans mémoire, on le remettrait indéfiniment. D'où PREFIXE_POSE et
 * PREFIXE_REFUS en sessionStorage. La mémoire s'efface dès que la règle cesse d'être
 * satisfaite, pour que le cadeau revienne si la cliente remonte au palier.
 */

const PREFIXE_POSE = 'gb:cadeau:pose:';
const PREFIXE_REFUS = 'gb:cadeau:refuse:';
const CLE_RECHARGES = 'gb:cadeau:recharges';

const RANGS = ['1', '2', '3', '4', '5', '6'];
const MAX_CORRECTIONS = 4;
const MAX_RECHARGES = 2;
const ATTENTE_MAX = 5000;

let enCours = null;
let corrections = 0;

/* --- Mémoire de session ---------------------------------------------------- */
/* sessionStorage peut lever en navigation privée : jamais sans garde. */

function lire(cle) {
  try {
    return sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

function noter(cle, valeur) {
  try {
    sessionStorage.setItem(cle, valeur);
  } catch {
    /* rien : la mémoire est un confort, pas une condition de fonctionnement */
  }
}

function oublier(cle) {
  try {
    sessionStorage.removeItem(cle);
  } catch {
    /* idem */
  }
}

/* --- Accès au panier -------------------------------------------------------- */

function route(nom, repli) {
  return window.Theme?.routes?.[nom] || repli;
}

async function poster(url, corps) {
  const reponse = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(corps),
    signal: AbortSignal.timeout(ATTENTE_MAX),
  });

  const donnees = await reponse.json().catch(() => null);
  if (!reponse.ok) {
    throw new Error((donnees && (donnees.description || donnees.message)) || `Panier : reponse ${reponse.status}`);
  }
  return donnees;
}

async function chargerPanier() {
  try {
    const reponse = await fetch(`${route('cart_url', '/cart')}.js`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(ATTENTE_MAX),
    });
    if (!reponse.ok) return null;
    return await reponse.json();
  } catch (erreur) {
    console.warn('[gb-cadeaux] panier illisible', erreur);
    return null;
  }
}

/* --- Le traitement ---------------------------------------------------------- */

class GbCadeauxPanier extends HTMLElement {
  connectedCallback() {
    if (this.dataset.agir !== 'true') return;
    synchroniser(this);
  }
}

function synchroniser(element) {
  if (enCours) return enCours;

  enCours = executer(element)
    .catch((erreur) => console.warn('[gb-cadeaux] synchronisation interrompue', erreur))
    .finally(() => {
      enCours = null;
    });

  return enCours;
}

async function executer(element) {
  const voulues = lireCharge(element);
  const panier = await chargerPanier();
  if (!panier) return;

  const presentes = new Map();
  for (const ligne of panier.items) {
    const rang = ligne.properties?._gb_regle;
    if (rang !== undefined && rang !== null) presentes.set(String(rang), ligne);
  }

  // Une règle qui n'est plus satisfaite repart de zéro : le cadeau reviendra si la
  // cliente remonte au palier, même si elle l'avait retiré à la main auparavant.
  for (const rang of RANGS) {
    if (!voulues.has(rang)) {
      oublier(PREFIXE_POSE + rang);
      oublier(PREFIXE_REFUS + rang);
    }
  }

  const aRetirer = [];
  for (const [rang, ligne] of presentes) {
    if (!voulues.has(rang)) aRetirer.push(ligne);
  }

  const aAjouter = [];
  for (const [rang, cadeau] of voulues) {
    if (presentes.has(rang)) {
      noter(PREFIXE_POSE + rang, 'true');
      continue;
    }
    if (lire(PREFIXE_REFUS + rang) === 'true') continue;
    if (lire(PREFIXE_POSE + rang) === 'true') {
      // Elle l'avait, elle ne l'a plus, et la règle reste satisfaite : retrait
      // volontaire. On ne le remet pas, sinon c'est un bras de fer avec la cliente.
      noter(PREFIXE_REFUS + rang, 'true');
      continue;
    }
    aAjouter.push(cadeau);
  }

  const attributAJour = (element.dataset.attributActuel || '') === (element.dataset.attributVoulu || '');

  if (aRetirer.length === 0 && aAjouter.length === 0 && attributAJour) {
    oublier(CLE_RECHARGES);
    return;
  }

  const boutons = bloquerCommander(element);

  try {
    if (aRetirer.length === 0 && aAjouter.length === 0) {
      await ecrireAttribut(element);
      return;
    }

    if (corrections >= MAX_CORRECTIONS) {
      console.warn('[gb-cadeaux] trop de corrections du panier sur cette page, arret de securite');
      return;
    }
    corrections += 1;

    let agi = false;

    for (const ligne of aRetirer) {
      if (await retirer(ligne)) agi = true;
    }

    if (aAjouter.length > 0 && (await ajouter(aAjouter))) agi = true;

    if (agi) await repeindre(element);
  } finally {
    boutons.forEach((bouton) => {
      bouton.disabled = false;
    });
  }
}

function lireCharge(element) {
  const voulues = new Map();

  let entrees = [];
  try {
    entrees = JSON.parse(element.dataset.cadeaux || '[]');
  } catch (erreur) {
    console.warn('[gb-cadeaux] regles illisibles', erreur);
    return voulues;
  }

  for (const entree of entrees) {
    if (entree && entree.rang && entree.variante) voulues.set(String(entree.rang), entree);
  }

  return voulues;
}

async function retirer(ligne) {
  try {
    await poster(route('cart_change_url', '/cart/change.js'), { id: ligne.key, quantity: 0 });
    return true;
  } catch (erreur) {
    console.warn('[gb-cadeaux] ligne cadeau non retiree', erreur);
    return false;
  }
}

async function ajouter(cadeaux) {
  const articles = cadeaux.map((cadeau) => {
    // _is_gift déclenche le rendu cadeau déjà présent dans le thème : style dédié,
    // badge, et sélecteur de quantité masqué (cart-drawer.liquid:218).
    // _gb_regle identifie nos lignes pour pouvoir les retirer.
    // Les propriétés préfixées d'un « _ » sont masquées au checkout par Shopify :
    // c'est la propriété nommée par Sandra, sans underscore, qui s'y affiche.
    const proprietes = { _is_gift: 'true', _gb_regle: String(cadeau.rang) };
    if (cadeau.badge) proprietes._gift_badge = cadeau.badge;
    if (cadeau.propriete) proprietes[cadeau.propriete] = cadeau.texte;

    return { id: cadeau.variante, quantity: 1, properties: proprietes };
  });

  try {
    await poster(route('cart_add_url', '/cart/add.js'), { items: articles });
    cadeaux.forEach((cadeau) => noter(PREFIXE_POSE + cadeau.rang, 'true'));
    return true;
  } catch (erreur) {
    // Produit épuisé, dépublié, supprimé… Sans ce garde-fou, la page panier se
    // rechargerait en boucle en tentant sans cesse le même ajout impossible.
    cadeaux.forEach((cadeau) => noter(PREFIXE_REFUS + cadeau.rang, 'true'));
    console.warn('[gb-cadeaux] cadeau non ajoute, regle mise en pause pour cette visite', erreur);
    return false;
  }
}

/**
 * Écrit l'attribut de commande et repeint, en une seule requête.
 * cart/update.js accepte « sections » et renvoie le panier complet : c'est tout ce
 * dont renderCartContents a besoin.
 */
async function repeindre(element) {
  const tiroir = document.querySelector('cart-drawer');
  const corps = {};

  const nom = element.dataset.attributNom;
  if (nom) corps.attributes = { [nom]: element.dataset.attributVoulu || '' };
  if (tiroir?.dataset.sectionId) corps.sections = tiroir.dataset.sectionId;

  let donnees = null;
  try {
    donnees = await poster(route('cart_update_url', '/cart/update.js'), corps);
  } catch (erreur) {
    console.warn('[gb-cadeaux] rafraichissement du panier impossible', erreur);
  }

  if (tiroir && donnees?.sections) {
    await tiroir.renderCartContents({ sections: donnees.sections, itemCount: donnees.item_count });
    return;
  }

  // Page panier : le thème lui-même recharge après chaque modification (cart.js:88).
  if (document.querySelector('cart-component')) recharger();
}

async function ecrireAttribut(element) {
  const nom = element.dataset.attributNom;
  if (!nom) return;

  const voulu = element.dataset.attributVoulu || '';

  try {
    await poster(route('cart_update_url', '/cart/update.js'), { attributes: { [nom]: voulu } });
    element.dataset.attributActuel = voulu;
  } catch (erreur) {
    console.warn('[gb-cadeaux] attribut de commande non enregistre', erreur);
  }
}

function recharger() {
  const compte = parseInt(lire(CLE_RECHARGES) || '0', 10) + 1;

  if (compte > MAX_RECHARGES) {
    console.warn('[gb-cadeaux] rechargements repetes de la page panier, arret de securite');
    return;
  }

  noter(CLE_RECHARGES, String(compte));
  window.location.reload();
}

/**
 * Empêche de partir en caisse pendant la correction, pour qu'aucune commande ne parte
 * sans ses cadeaux. Ne rend la main qu'aux boutons que l'on a bloqués.
 */
function bloquerCommander(element) {
  const racine = element.closest('cart-drawer') || element.closest('cart-component') || document;
  const boutons = Array.from(racine.querySelectorAll('[data-ref="checkout-button"]')).filter((bouton) => !bouton.disabled);

  boutons.forEach((bouton) => {
    bouton.disabled = true;
  });

  return boutons;
}

if (!customElements.get('gb-cadeaux-panier')) {
  customElements.define('gb-cadeaux-panier', GbCadeauxPanier);
}
